import { useState } from "react";
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
} from "reactstrap";

import {
    FiActivity,
    FiArrowRight,
    FiBarChart2,
    FiCheckCircle,
    FiChevronRight,
    FiCloud,
    FiCode,
    FiCpu,
    FiDatabase,
    FiEye,
    FiLayers,
    FiMonitor,
    FiPlayCircle,
    FiServer,
    FiSettings,
    FiZap,
} from "react-icons/fi";

import GetInTouch from "../../components/common/Contact/GetInTouch";

import "./ImageClassificationProjectPage.css";
import SEO from "../../components/common/seo/SEO";


const ImageClassificationProjectPage = () => {
    const [openFaq, setOpenFaq] = useState("");

    const toggleFaq = (id) => {
        setOpenFaq(openFaq === id ? "" : id);
    };

    const prerequisites = [
        {
            icon: <FiCode />,
            title: "Programming Knowledge",
            description:
                "Intermediate to advanced knowledge of Python or any programming language.",
        },
        {
            icon: <FiSettings />,
            title: "Programming Logic",
            description:
                "Familiarity with loops, functions, arrays and fundamental programming concepts.",
        },
        {
            icon: <FiEye />,
            title: "Image Classification",
            description:
                "General familiarity with classification or image-based tasks is optional but helpful.",
        },
    ];

    const technologies = [
        {
            icon: <FiCpu />,
            title: "Deep Learning",
            value: "TensorFlow / Keras",
        },
        {
            icon: <FiCloud />,
            title: "Deployment",
            value: "Google Cloud Platform",
        },
        {
            icon: <FiDatabase />,
            title: "Data Handling",
            value: "Pandas / NumPy",
        },
        {
            icon: <FiBarChart2 />,
            title: "Visualization",
            value: "Matplotlib / Seaborn",
        },
        {
            icon: <FiMonitor />,
            title: "Environment",
            value: "Jupyter / Colab / VS Code",
        },
        {
            icon: <FiEye />,
            title: "Image Processing",
            value: "OpenCV",
        },
        {
            icon: <FiLayers />,
            title: "Classical ML",
            value: "Scikit-learn",
        },
        {
            icon: <FiActivity />,
            title: "Feature Extraction",
            value: "HOG",
        },
        {
            icon: <FiServer />,
            title: "Classification",
            value: "Support Vector Machine",
        },
        {
            icon: <FiZap />,
            title: "Deep Learning Concepts",
            value: "Tensors / Gradient Descent",
        },
    ];

    const learningObjectives = [
        "Understand the difference between image processing and computer vision",
        "Represent, manipulate, and analyze images as matrices using NumPy and OpenCV",
        "Extract image features using HOG and build classifiers using SVM and logistic regression",
        "Grasp core AI concepts such as tensors, gradient descent, and designing ML systems",
        "Build, train, and evaluate Artificial Neural Networks (ANN) using TensorFlow",
        "Apply deep learning to solve real-world problems like malaria diagnosis",
        "Prepare, visualize, and process medical image data for training neural networks",
        "Deploy a complete deep learning product to Google Cloud Platform (GCP)",
    ];

    const modules = [
        {
            number: "01",
            color: "blue",
            title: "Introduction to Computer Vision and Image Processing",
            duration: "Module 1",
            topics: [
                "Image Processing vs Computer Vision",
                "Problems in Computer Vision",
                "How is an Image Formed",
                "Digital Images",
                "Images as Matrix",
                "Manipulating Pixels",
                "Color Images",
            ],
        },
        {
            number: "02",
            color: "green",
            title: "Image Classification",
            duration: "Module 2",
            topics: [
                "Introduction",
                "Histogram of Oriented Gradients (HOG)",
                "Support Vector Machine (SVM)",
                "Logistic Regression",
            ],
        },
        {
            number: "03",
            color: "blue",
            title: "Introduction to Artificial Intelligence",
            duration: "Module 3",
            topics: [
                "Introduction to AI",
                "Designing a Machine Learning System",
                "NumPy",
                "Introduction to Tensors",
                "Gradient Descent",
            ],
        },
        {
            number: "04",
            color: "green",
            title: "Building Artificial Neural Networks (ANN) using TensorFlow",
            duration: "Module 4",
            topics: [
                "Understanding Neural Networks",
                "Building Blocks of Neural Networks",
                "Bias-Variance Trade-off",
                "How to Handle Overfitting",
            ],
        },
        {
            number: "05",
            color: "blue",
            title: "Malaria Diagnosis using Deep Learning",
            duration: "Module 5",
            topics: [
                "Data Preparation",
                "Data Visualization",
                "Data Processing",
                "Building Neural Network for Diagnosis",
                "Training",
                "Testing",
                "Validation",
                "Deployment of End-to-End Product to Google Cloud Platform",
            ],
        },
    ];

    const faqs = [
        {
            id: "faq-1",
            question:
                "I’ve wasted money on courses before. How is this different?",
            answer:
                "This is not just theory. You’ll build a fully working AI-powered IT support automation system — step-by-step, guided by real professionals. It’s practical, project-based, and portfolio-ready.",
        },
        {
            id: "faq-2",
            question:
                "I’m not confident in coding yet. Can I still join?",
            answer:
                "If you’ve completed some basic programming (especially through our beginner courses), you’re good to go! If not, start with our foundation courses, then return for this project.",
        },
        {
            id: "faq-3",
            question:
                "Is this project useful for job interviews or placements?",
            answer:
                "Absolutely. You’ll finish with a deployable IT automation project, integrated with GitHub and real-time logging — a great addition to your resume and GitHub portfolio.",
        },
        {
            id: "faq-4",
            question: "Is this course beginner-friendly?",
            answer:
                "It’s beginner-friendly if you’ve completed basic to intermediate programming. If not, take our intro courses first — they’re designed to prepare you for this exact project.",
        },
        {
            id: "faq-5",
            question: "What tools do I need before starting?",
            answer:
                "Just install Python, Node.js, and VS Code — we’ll guide you through setup in the first session. Everything else will be covered during the course.",
        },
        {
            id: "faq-6",
            question: "Is this really worth my time?",
            answer:
                "In just 10 focused hours, you’ll learn email automation, GitHub API, WebSocket, and React integration — tools used by real tech teams today.",
        },
        {
            id: "faq-7",
            question: "Will I receive a certificate after the course?",
            answer:
                "Yes, you will receive an industry-recognized certificate.",
        },
        {
            id: "faq-8",
            question:
                "Are you looking to hire candidates for companies?",
            answer:
                "Yes, we specialize in helping businesses find and hire the right talent. Our HR and recruitment services are tailored to meet your company’s specific hiring needs. Contact us to learn how we can support your hiring process.",
        },
    ];

    const relatedProjects = [
        {
            icon: <FiActivity />,
            title:
                "Intelligent Manufacturing Quality Control System For Tire Production",
            description:
                "Developed an AI-powered quality control system for tyre manufacturing using computer vision to monitor real-time production line videos. The system detects anomalies in the production sequence and triggers a GenAI pipeline for root cause analysis. Hosted on AWS, it enhances manufacturing efficiency and quality compliance.",
            link:
                "https://feesworth.com/ai-ml-project-intelligent-manufacturing-quality-control-system-for-tire-production/",
        },
        {
            icon: <FiServer />,
            title: "IT Support Agentic AI",
            description:
                "Build an AI-powered IT support automation system that processes email-based IT requests, identifies intents (e.g., GitHub access/revoke), creates tickets in a ticketing system (e.g., mock ADO/ServiceNow), performs GitHub actions, and displays activity logs in a frontend.",
            link:
                "/replace-it-help-desk-with-automated-ticketing-system-using-ai",
        },
        {
            icon: <FiDatabase />,
            title: "Rag (Retrieval Agument Generation)",
            description:
                "Optimized large language model outputs by integrating authoritative external knowledge bases, enhancing accuracy and relevance beyond pre-trained data sources.",
            link:
                "/ai-ml-project-rag-retrieval-argument-generation",
        },
        {
            icon: <FiLayers />,
            title: "Brain Stroke Prediction using OpenCV and CNN",
            description:
                "Developed an AI-based brain stroke prediction system using OpenCV and CNNs to analyze MRI/CT scans. The model automates stroke detection with high accuracy by extracting spatial features from medical images. Built with TensorFlow/Keras, the solution streamlines diagnosis and supports clinical decision-making.",
            link:
                "/ai-ml-project-brain-stroke-prediction-opencv-cnn",
        },
    ];

    return (
        <>
            <SEO
                title="Image Classification Project | HOG, SVM & Deep Learning | FeesWorth"
                description="Build an image classification project using computer vision, HOG feature extraction, SVM, TensorFlow, neural networks, and Google Cloud Platform. Learn image processing, classical machine learning, deep learning, and deployment."
                keywords="Image Classification Project, Image Classification using HOG and SVM, Computer Vision Project, HOG Feature Extraction, SVM Image Classification, Deep Learning Project, TensorFlow Project, Neural Network Project, Google Cloud AI Project"
                canonical="https://feesworth.com/ai-ml-project-image-classification-deep-learning"
            />

            <div className="image-classification-page">

                {/* =====================================================
                HERO
            ===================================================== */}
                <section className="image-classification-hero">

                    <div className="image-classification-hero-grid" />

                    <Container>

                        <Row className="align-items-center">

                            <Col lg="7">

                                <div className="image-classification-hero-content">

                                    <Badge className="image-classification-eyebrow">
                                        AI / ML PROJECT
                                    </Badge>

                                    <h1>
                                        Image Classification
                                        <span>
                                            Using Naive & Deep Learning Techniques
                                        </span>
                                    </h1>

                                    <p className="image-classification-hero-description">
                                        Build an efficient image classification
                                        pipeline using classical computer vision,
                                        HOG feature extraction, SVM classification,
                                        and deep learning techniques.
                                    </p>

                                    <div className="image-classification-hero-tags">

                                        <span>
                                            <FiEye />
                                            Computer Vision
                                        </span>

                                        <span>
                                            <FiActivity />
                                            HOG
                                        </span>

                                        <span>
                                            <FiLayers />
                                            SVM
                                        </span>

                                        <span>
                                            <FiCpu />
                                            Neural Networks
                                        </span>

                                        <span>
                                            <FiCloud />
                                            GCP
                                        </span>

                                    </div>

                                    <div className="image-classification-hero-actions">

                                        <Button
                                            color="primary"
                                            className="image-classification-primary-btn"
                                            href="#image-classification-contact"
                                        >
                                            Let Us Call You
                                            <FiArrowRight />
                                        </Button>

                                        <Button
                                            outline
                                            className="image-classification-secondary-btn"
                                            href="#image-classification-curriculum"
                                        >
                                            <FiPlayCircle />
                                            Explore Project
                                        </Button>

                                    </div>

                                    <div className="image-classification-hero-meta">

                                        <div>
                                            <strong>5 Modules</strong>
                                            <span>Project Curriculum</span>
                                        </div>

                                        <div>
                                            <strong>HOG + SVM</strong>
                                            <span>Classical Vision</span>
                                        </div>

                                        <div>
                                            <strong>ANN + GCP</strong>
                                            <span>Deep Learning</span>
                                        </div>

                                    </div>

                                </div>

                            </Col>

                            <Col lg="5">

                                <div className="image-classification-visual">

                                    <div className="classification-visual-header">

                                        <div>
                                            <span className="classification-live-dot" />
                                            IMAGE PIPELINE
                                        </div>

                                        <FiActivity />

                                    </div>

                                    <div className="classification-image-preview">

                                        <div className="classification-image-frame">

                                            <div className="classification-grid" />

                                            <div className="classification-object">
                                                <span />
                                                <span />
                                                <span />
                                                <span />
                                            </div>

                                            <div className="classification-hog-lines">
                                                <i />
                                                <i />
                                                <i />
                                                <i />
                                                <i />
                                            </div>

                                        </div>

                                        <span className="classification-image-label">
                                            INPUT IMAGE
                                        </span>

                                    </div>

                                    <div className="classification-pipeline">

                                        <div>
                                            <FiEye />
                                            <span>Image</span>
                                        </div>

                                        <FiChevronRight />

                                        <div>
                                            <FiActivity />
                                            <span>HOG</span>
                                        </div>

                                        <FiChevronRight />

                                        <div>
                                            <FiLayers />
                                            <span>SVM</span>
                                        </div>

                                        <FiChevronRight />

                                        <div>
                                            <FiCpu />
                                            <span>ANN</span>
                                        </div>

                                    </div>

                                    <div className="classification-result">

                                        <div>
                                            <span>PIPELINE STATUS</span>
                                            <strong>Classification Ready</strong>
                                        </div>

                                        <FiCheckCircle />

                                    </div>

                                </div>

                            </Col>

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                PROJECT OVERVIEW
            ===================================================== */}
                <section className="image-classification-section image-classification-overview">

                    <Container>

                        <div className="image-classification-section-heading">

                            <span>PROJECT OVERVIEW</span>

                            <h2>
                                What is this project all about?
                            </h2>

                            <p>
                                Learn how classical computer vision and deep
                                learning techniques can be combined to create
                                practical image classification systems.
                            </p>

                        </div>

                        <Row className="align-items-center">

                            <Col lg="7">

                                <div className="image-classification-overview-content">

                                    <div className="image-classification-highlight">

                                        <FiLayers />

                                        <div>

                                            <strong>
                                                Classical Computer Vision Pipeline
                                            </strong>

                                            <span>
                                                HOG feature extraction followed
                                                by SVM-based image classification
                                            </span>

                                        </div>

                                    </div>

                                    <p>
                                        This project focuses on developing an
                                        efficient image classification system
                                        using a classical computer vision
                                        pipeline.
                                    </p>

                                    <p>
                                        The classification is performed using
                                        Support Vector Machines (SVM) after
                                        extracting meaningful features from
                                        input images using the Histogram of
                                        Oriented Gradients (HOG) descriptor.
                                    </p>

                                    <p>
                                        The project also introduces artificial
                                        neural networks and deep learning,
                                        progressing from image processing and
                                        classical machine learning to an
                                        end-to-end deep learning product.
                                    </p>

                                </div>

                            </Col>

                            <Col lg="5">

                                <Card className="image-classification-scope-card">

                                    <CardBody>

                                        <div className="image-classification-scope-icon">
                                            <FiLayers />
                                        </div>

                                        <h3>
                                            Project Scope
                                        </h3>

                                        <div className="classification-scope-item">
                                            <FiCheckCircle />
                                            <span>
                                                Image preprocessing
                                            </span>
                                        </div>

                                        <div className="classification-scope-item">
                                            <FiCheckCircle />
                                            <span>
                                                HOG feature extraction
                                            </span>
                                        </div>

                                        <div className="classification-scope-item">
                                            <FiCheckCircle />
                                            <span>
                                                SVM classification
                                            </span>
                                        </div>

                                        <div className="classification-scope-item">
                                            <FiCheckCircle />
                                            <span>
                                                Neural network development
                                            </span>
                                        </div>

                                        <div className="classification-scope-item">
                                            <FiCheckCircle />
                                            <span>
                                                End-to-end GCP deployment
                                            </span>
                                        </div>

                                    </CardBody>

                                </Card>

                            </Col>

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                PREREQUISITES
            ===================================================== */}
                <section className="image-classification-section image-classification-prerequisites">

                    <Container>

                        <div className="image-classification-section-heading">

                            <span>GET READY</span>

                            <h2>
                                What You Should Know Before Starting
                            </h2>

                            <p>
                                A programming foundation will help you work
                                comfortably with the image processing and
                                machine learning concepts in this project.
                            </p>

                        </div>

                        <Row>

                            {prerequisites.map((item, index) => (

                                <Col
                                    md="6"
                                    lg="4"
                                    key={index}
                                    className="mb-4"
                                >

                                    <Card className="image-classification-info-card">

                                        <CardBody>

                                            <div className="image-classification-card-icon">
                                                {item.icon}
                                            </div>

                                            <h3>
                                                {item.title}
                                            </h3>

                                            <p>
                                                {item.description}
                                            </p>

                                        </CardBody>

                                    </Card>

                                </Col>

                            ))}

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                TECHNOLOGIES
            ===================================================== */}
                <section className="image-classification-section image-classification-technologies">

                    <Container>

                        <div className="image-classification-section-heading">

                            <span>TECH STACK</span>

                            <h2>
                                Tools & Technologies
                            </h2>

                            <p>
                                What tools and technologies will be used
                                in this project.
                            </p>

                        </div>

                        <Row>

                            {technologies.map((technology, index) => (

                                <Col
                                    sm="6"
                                    md="4"
                                    lg="3"
                                    key={index}
                                    className="mb-4"
                                >

                                    <Card className="image-classification-tech-card">

                                        <CardBody>

                                            <div className="image-classification-tech-icon">
                                                {technology.icon}
                                            </div>

                                            <span>
                                                {technology.title}
                                            </span>

                                            <strong>
                                                {technology.value}
                                            </strong>

                                        </CardBody>

                                    </Card>

                                </Col>

                            ))}

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                LEARNING OBJECTIVES
            ===================================================== */}
                <section className="image-classification-section image-classification-objectives">

                    <Container>

                        <Row className="align-items-center">

                            <Col lg="5">

                                <div className="image-classification-objectives-heading">

                                    <span>
                                        LEARNING OBJECTIVES
                                    </span>

                                    <h2>
                                        What you will learn in this course
                                    </h2>

                                    <p>
                                        Progress from fundamental image
                                        processing concepts to classical
                                        classification, neural networks and
                                        cloud deployment.
                                    </p>

                                </div>

                            </Col>

                            <Col lg="7">

                                <div className="image-classification-objectives-list">

                                    {learningObjectives.map(
                                        (objective, index) => (

                                            <div
                                                className="image-classification-objective-item"
                                                key={index}
                                            >

                                                <div>
                                                    <FiCheckCircle />
                                                </div>

                                                <span>
                                                    {objective}
                                                </span>

                                            </div>

                                        )
                                    )}

                                </div>

                            </Col>

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                LESSON PLAN
            ===================================================== */}
                <section
                    className="image-classification-section image-classification-curriculum"
                    id="image-classification-curriculum"
                >

                    <Container>

                        <div className="image-classification-section-heading">

                            <span>LESSON PLAN</span>

                            <h2>
                                Build Your Skills Step by Step
                            </h2>

                            <p>
                                Follow a structured path from image processing
                                fundamentals through classical machine learning,
                                neural networks and deep learning deployment.
                            </p>

                        </div>

                        <div className="image-classification-timeline">

                            {modules.map((module) => (

                                <div
                                    className={`image-classification-module ${module.color}`}
                                    key={module.number}
                                >

                                    <div className="image-classification-module-number">
                                        {module.number}
                                    </div>

                                    <Card className="image-classification-module-card">

                                        <CardBody>

                                            <div className="image-classification-module-header">

                                                <div>

                                                    <span className="image-classification-module-label">
                                                        {module.duration}
                                                    </span>

                                                    <h3>
                                                        {module.title}
                                                    </h3>

                                                </div>

                                                <div className="image-classification-module-icon">
                                                    <FiCode />
                                                </div>

                                            </div>

                                            <div className="image-classification-topic-grid">

                                                {module.topics.map(
                                                    (topic, topicIndex) => (

                                                        <div
                                                            className="image-classification-topic"
                                                            key={topicIndex}
                                                        >

                                                            <FiCheckCircle />

                                                            <span>
                                                                {topic}
                                                            </span>

                                                        </div>

                                                    )
                                                )}

                                            </div>

                                        </CardBody>

                                    </Card>

                                </div>

                            ))}

                        </div>

                    </Container>

                </section>


                {/* =====================================================
                FAQ
            ===================================================== */}
                <section className="image-classification-section image-classification-faq">

                    <Container>

                        <div className="image-classification-section-heading">

                            <span>FAQ</span>

                            <h2>
                                Frequently Asked Questions
                            </h2>

                            <p>
                                Everything you need to know before starting
                                the Image Classification project.
                            </p>

                        </div>

                        <div className="image-classification-faq-wrapper">

                            <Accordion
                                open={openFaq}
                                toggle={toggleFaq}
                            >

                                {faqs.map((faq) => (

                                    <AccordionItem key={faq.id}>

                                        <AccordionHeader targetId={faq.id}>
                                            {faq.question}
                                        </AccordionHeader>

                                        <AccordionBody accordionId={faq.id}>
                                            {faq.answer}

                                            {faq.id === "faq-8" && (

                                                <p className="image-classification-faq-link">

                                                    Please visit our page:{" "}

                                                    <a
                                                        href="https://wocons.com/"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        WoCons – Custom AI &
                                                        CAD Solutions | Bengaluru
                                                    </a>

                                                </p>

                                            )}

                                        </AccordionBody>

                                    </AccordionItem>

                                ))}

                            </Accordion>

                        </div>

                    </Container>

                </section>


                {/* =====================================================
                RELATED PROJECTS
            ===================================================== */}
                <section className="image-classification-section image-classification-related">

                    <Container>

                        <div className="image-classification-section-heading">

                            <span>EXPLORE MORE</span>

                            <h2>
                                Explore More AI Projects
                            </h2>

                            <p>
                                Continue exploring practical AI and machine
                                learning projects.
                            </p>

                        </div>

                        <Row>

                            {relatedProjects.map(
                                (project, index) => (

                                    <Col
                                        lg="6"
                                        key={index}
                                        className="mb-4"
                                    >

                                        <Card className="image-classification-related-card">

                                            <CardBody>

                                                <div className="image-classification-related-icon">
                                                    {project.icon}
                                                </div>

                                                <h3>
                                                    {project.title}
                                                </h3>

                                                <p>
                                                    {project.description}
                                                </p>

                                                <a
                                                    href={project.link}
                                                    className="image-classification-related-link"
                                                >
                                                    View Course Page
                                                    <FiArrowRight />
                                                </a>

                                            </CardBody>

                                        </Card>

                                    </Col>

                                )
                            )}

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                CONTACT
            ===================================================== */}
                <section
                    className="image-classification-contact"
                    id="image-classification-contact"
                >

                    <Container>

                        <div className="image-classification-contact-heading">

                            <span>
                                READY TO BUILD?
                            </span>

                            <h2>
                                Let's Discuss Your Image Classification Project
                            </h2>

                            <p>
                                Have questions about the project, tools,
                                prerequisites, or training? Send us your
                                details and our team will get back to you.
                            </p>

                        </div>

                        <div className="image-classification-contact-wrapper">

                            <GetInTouch
                                courseName="Image Classification Using Naive & Deep Learning Techniques"
                                title="Get in Touch"
                                subtitle="Interested in building this image classification project? Send us your details and our team will contact you."
                            />

                        </div>

                    </Container>

                </section>

            </div>
        </>


    );
};

export default ImageClassificationProjectPage;